import React, {memo, useRef, useState, useCallback} from 'react';
import {RgbaColor} from 'react-colorful';
import cn from 'classnames';
import {useOutsideClick} from '@/common/helpers/hooks/useOutsideClick';
import {useGlobalHotkey} from '@/common/helpers/hooks/useGlobalHotkey';
import {ColorPicker, getCssColor} from '@/common/components/ColorPicker';
import styles from './ColorPickButton.css';

interface ColorPickButtonProps {
	className?: string;
	value: RgbaColor;
	onChange(value: RgbaColor): void;
}

export const ColorPickButton = memo<ColorPickButtonProps>(props => {
	const {className, value, onChange} = props;

	const pickerRef = useRef<HTMLDivElement>(null);

	const [opened, setOpened] = useState(false);

	const handleOpenSwitch = () => {
		setOpened(!opened);
	};

	const handleClose = useCallback(() => {
		setOpened(false);
	}, []);

	useOutsideClick(pickerRef, handleClose, opened);
	useGlobalHotkey('Escape', handleClose, opened);

	return (
		<div className={cn(styles.root, className)}>
			<button
				className={styles.target}
				type='button'
				style={{background: getCssColor(value)}}
				onClick={handleOpenSwitch}
			/>
			{opened && (
				<ColorPicker
					ref={pickerRef}
					className={styles.picker}
					value={value}
					onChange={onChange}
					onClose={handleClose}
				/>
			)}
		</div>
	);
});

ColorPickButton.displayName = 'ColorPickButton';
